import { useEffect, useState } from "react";
import type { ReactNode } from "react";

// SuccessMoment — the calm ripple-check confirmation shared by both flows'
// sent screens (presession/postsession). Plays the entrance ONCE on mount
// (disc pop → tick draw → two ripples), then settles to the static visible
// state so later re-renders never replay. Keyframes live inside the component
// (tokens.css untouched — contracts §5); prefers-reduced-motion mounts settled.

export interface SuccessMomentProps {
    title: string;
    body?: string;
    /** CTA slot (e.g. the "Back to home" button). */
    children?: ReactNode;
    /** Body measure. `narrow` (default) caps it at 30ch. `container` lets it run to 85%
     *  of the card instead, for bodies long enough that 30ch breaks them into short,
     *  choppy lines (a privacy line, a confirmation with detail). */
    bodyWidth?: "narrow" | "container";
}

const PLAY_MS = 1700;
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
    return typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia(REDUCED_MOTION).matches
        : false;
}

// Scoped keyframes + play-state rules (from the hi-fi's .ps-success block).
const KEYFRAMES = `
@keyframes dova-sm-disc-in { 0% { transform: scale(0.7); opacity: 0; } 60% { transform: scale(1.05); } 100% { transform: scale(1); opacity: 1; } }
@keyframes dova-sm-draw-tick { from { stroke-dashoffset: 42; } to { stroke-dashoffset: 0; } }
@keyframes dova-sm-ripple { 0% { transform: scale(0.55); opacity: 0.5; } 100% { transform: scale(1.55); opacity: 0; } }
[data-play="true"] .dova-sm-disc { animation: dova-sm-disc-in 0.6s ease backwards; }
[data-play="true"] .dova-sm-tick { animation: dova-sm-draw-tick 0.5s ease 0.34s backwards; }
[data-play="true"] .dova-sm-ripple-1 { animation: dova-sm-ripple 1.2s ease 0.42s; }
[data-play="true"] .dova-sm-ripple-2 { animation: dova-sm-ripple 1.2s ease 0.62s; }
@media (prefers-reduced-motion: reduce) {
  [data-play="true"] .dova-sm-disc, [data-play="true"] .dova-sm-tick,
  [data-play="true"] .dova-sm-ripple-1, [data-play="true"] .dova-sm-ripple-2 { animation: none; }
}
`;

export function SuccessMoment({ title, body, children, bodyWidth = "narrow" }: SuccessMomentProps) {
    const [play, setPlay] = useState(false);

    useEffect(() => {
        if (prefersReducedMotion()) return;
        setPlay(true);
        const timer = setTimeout(() => setPlay(false), PLAY_MS);
        return () => clearTimeout(timer);
        // Mount-only by design: the moment plays once, re-renders never replay.
    }, []);

    return (
        <div data-play={play ? "true" : "false"} className="flex flex-1 flex-col text-center">
            <style>{KEYFRAMES}</style>
            <div className="flex flex-1 flex-col items-center justify-center px-2">
                {/* 120px for a 120-unit viewBox, so units are pixels. Disc r36 (72px) balances the
                    30px h1; the tick keeps its shape and draw-in, so at stroke 3.5 it sits at
                    ~37% of the disc, the classic check-in-a-circle ratio (it was 44% at stroke 4
                    on a 54px disc, which read heavy). Ripples start at r44 to clear the disc. */}
                <svg viewBox="0 0 120 120" aria-hidden="true" className="mb-6 h-[120px] w-[120px] overflow-visible">
                    <circle
                        className="dova-sm-ripple-1 [transform-box:fill-box] origin-center"
                        cx="60"
                        cy="60"
                        r="44"
                        fill="none"
                        stroke="var(--interactive)"
                        strokeWidth="1.5"
                        opacity="0"
                    />
                    <circle
                        className="dova-sm-ripple-2 [transform-box:fill-box] origin-center"
                        cx="60"
                        cy="60"
                        r="44"
                        fill="none"
                        stroke="var(--interactive)"
                        strokeWidth="1.5"
                        opacity="0"
                    />
                    <circle
                        className="dova-sm-disc [transform-box:fill-box] origin-center"
                        cx="60"
                        cy="60"
                        r="36"
                        fill="var(--success-bg)"
                        stroke="var(--success-bd)"
                        strokeWidth="1.5"
                    />
                    <path
                        className="dova-sm-tick"
                        d="M47 61 l8 8 l19 -21"
                        fill="none"
                        stroke="var(--success)"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="42"
                    />
                </svg>
                <h1 className="m-0 [font:var(--type-h1)] text-primary">{title}</h1>
                {body ? (
                    <p className={`mx-auto mb-0 mt-4 ${bodyWidth === "container" ? "max-w-[85%]" : "max-w-[30ch]"} [font:var(--type-body)] text-secondary`}>{body}</p>
                ) : null}
            </div>
            {children}
        </div>
    );
}
